import { useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  CreditCard,
  Loader2,
  Crown,
  FileText,
  Gauge,
  Minus,
  Plus,
  Shield,
  Star,
  Trash2,
  Wallet,
  X,
} from "lucide-react";

import { AddCardForm } from "@/components/billing/add-card-form";
import { formatPrice } from "@/components/billing/format-price";
import { BillingProfileForm } from "@/components/billing/billing-profile-form";
import {
  ChangePlanSheet,
  FreePlanCard,
  ScheduledChangeNotice,
} from "@/components/billing/change-plan-sheet";
import { CheckoutModal } from "@/components/billing/checkout";
import { CoveragePlanCard } from "@/components/billing/coverage-plan-card";
import type { BillingWorkspace, PricingTier } from "@/components/billing/checkout/checkout-types";
import { InvoicesSection } from "@/components/billing/invoices-section";
import {
  CardActionDeclined,
  CardActionOutcomeUnknown,
  CardActionStillProcessing,
  discardPendingPayment,
  PendingChoiceCancelled,
  sendChangeWithCardAction,
} from "@/components/billing/confirm-card-action";
import { OneClickPaymentModal } from "@/components/billing/one-click-payment-modal";
import { usePendingPaymentPrompt } from "@/components/billing/pending-payment-sheet";
import {
  PROVIDER_PLAN_ROWS_KEY,
  useProviderPlanRows,
} from "@/components/billing/provider-plan-rows";
import {
  clinicQuote,
  hasPerSeatTiers,
  MAX_CLINIC_DOCTORS,
  MAX_NON_SEAT_QUANTITY,
} from "@/components/pricing/clinic-seat-pricing";
import { ServicePauseCard } from "@/components/billing/service-pause-card";
import {
  GoToServicePauseButton,
  isServicesPausedError,
  scrollToServicePauseCard,
  SERVICE_PAUSE_CARD_ID,
  usePauseCarryBillingMessage,
  useServicesPausedBillingMessage,
} from "@/components/billing/services-paused-billing";
import { StripeElementsProvider } from "@/components/billing/stripe-elements-provider";
import { SubscriptionExpandedCard } from "@/components/billing/subscription-expanded-card";
import { SubscriptionSeats } from "@/components/billing/subscription-seats";
import { UsageDashboard } from "@/components/billing/usage-dashboard";
import {
  ProtectedContent,
  useVaultLock,
} from "@/components/account/medical-vault/protected-content";
import { ProtectionLockIcon } from "@/components/account/protection-lock-icon";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  isVaultUnlockCancelled,
  useProtectedAction,
  withProtectedSavingToast,
} from "@/lib/hooks/use-protected-action";
import { SERVICE_PAUSE_KEY_ROOT } from "@/lib/hooks/use-service-pause";
import { calculatePlanPrice } from "@/lib/pricing/calculate-plan-price";
import { showToast, withSavingToast } from "@/lib/ui/toast";
import { handleVaultLocked, isVaultLockedError } from "@/lib/vault-unlock";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";

// Legacy tab set + order (src/components/billing/billing-page-tabs.tsx):
// Subscriptions · Invoices · Cards · Usage.
export type BillingTab = "subscription" | "invoices" | "cards" | "usage";
export const BILLING_VALID_TABS: readonly BillingTab[] = [
  "subscription",
  "invoices",
  "cards",
  "usage",
] as const;

interface Subscription {
  id: string;
  entityType: string;
  entityId: string;
  planTier: string;
  planInterval: string;
  price: number;
  currency: string;
  /** `paused`: services paused by the owner (`_docs/service-pause.md`). */
  status: "active" | "trialing" | "past_due" | "cancelled" | "paused";
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelledAt: string | null;
  isFounding: boolean;
  isGrandfathered: boolean;
  quantity: number;
  plan: {
    code: string;
    name: Record<string, string>;
    features: Record<string, boolean>;
  } | null;
  /** Stripe bills it: a downgrade waits for the period end. Optional until the API ships it. */
  billedByStripe?: boolean;
  /** A downgrade waiting for the period end (`planName` is a LocalizedString). */
  scheduledChange?: {
    planCode: string;
    planName: unknown;
    effectiveAt: string;
  } | null;
}

interface BillingResponse {
  subscriptions: Subscription[];
  payments: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    paidAt: string | null;
  }>;
  wallet: {
    balance: number;
    payFromWallet: boolean;
    /** Proration credit — spent on the next renewals automatically, never paid out. */
    subscriptionCredit?: number;
  };
}

interface PaymentCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

interface CardsResponse {
  cards: PaymentCard[];
  defaultPaymentMethodId: string | null;
}

/** Legacy subscription-section URL sub-state (`?subtab=detail&subId=…` +
 *  `?category=` auto-opens the checkout modal). Billing routes spread this
 *  into their validateSearch so the params survive navigation. */
export interface BillingSubSearch {
  subtab?: "detail";
  subId?: string;
  category?: string;
}

export function validateBillingSubSearch(search: Record<string, unknown>): BillingSubSearch {
  return {
    subtab: search.subtab === "detail" ? "detail" : undefined,
    subId: typeof search.subId === "string" && search.subId ? search.subId : undefined,
    category: typeof search.category === "string" && search.category ? search.category : undefined,
  };
}

export type { BillingWorkspace };

interface UnifiedBillingViewProps {
  activeTab: BillingTab;
  onTabChange: (tab: BillingTab) => void;
  workspace?: BillingWorkspace;
}

/**
 * Shared billing surface (subscriptions / cards / invoices).
 *
 * Per legacy `UnifiedBillingContent` rationale: the user is the only
 * payer, so all 5 workspace billing URLs (account/billing,
 * account/user/billing, account/provider/billing,
 * account/facility/[id]/billing, account/business/[id]/billing) render
 * IDENTICAL content. Each route file owns its own URL state + tab
 * navigation; they all consume this component.
 */
export function UnifiedBillingView({ activeTab, onTabChange, workspace }: UnifiedBillingViewProps) {
  const { t } = useLingui();
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  // Legacy tab labels + icons (billing-page-tabs.tsx): Crown / FileText /
  // CreditCard (+ protection lock) / Gauge.
  const tabs: TabItem<BillingTab>[] = [
    { id: "subscription", label: t`Subscriptions`, icon: <Crown className="size-4" /> },
    { id: "invoices", label: t`Invoices`, icon: <FileText className="size-4" /> },
    {
      id: "cards",
      label: t`Cards`,
      icon: (
        <span className="flex items-center gap-1">
          <CreditCard className="size-4" />
          <ProtectionLockIcon isProtected className="size-3" />
        </span>
      ),
    },
    { id: "usage", label: t`Usage`, icon: <Gauge className="size-4" /> },
  ];

  return (
    <div>
      {/* Shared page header (P27): same h1 as every account page; TabBar
          mt-6 carries the spacing, so no mb on the header. */}
      <PageHeader
        title={<Trans>Subscription & Billing</Trans>}
        description={<Trans>Manage your subscription, invoices, and payment methods</Trans>}
      />

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-6"
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={BILLING_VALID_TABS}
        onTabChange={onTabChange}
        className="mt-4"
        renderPanel={(id) => {
          if (id === "subscription") return <SubscriptionPanel workspace={workspace} />;
          if (id === "invoices") {
            // Legacy order: fiscal billing profile above the invoice list.
            return (
              <div className="space-y-6 pb-6">
                <BillingProfileForm />
                <InvoicesSection />
              </div>
            );
          }
          if (id === "cards") return <CardsPanel />;
          if (id === "usage") return <UsageDashboard />;
          return null;
        }}
      />
    </div>
  );
}

const BILLING_KEY = ["api", "/api/account/billing"] as const;

type SubscriptionView = "summary" | "detail";

interface CheckoutModalState {
  open: boolean;
  planCode?: string;
  planData?: PricingTier;
  entityType?: string;
  interval?: "monthly" | "annual";
  /** Plan-step category; the account-wide smart default when omitted. */
  category?: string;
}

/**
 * 1:1 port of legacy `src/components/billing/subscription-section.tsx`
 * (user scope): summary view (expanded card per subscription + wallet
 * toggle) and detail view (`?subtab=detail&subId=…`) with plan actions,
 * seats, past-due retry, cancel/reactivate. All plan changes happen inside
 * the in-site CheckoutModal — no external Stripe portal.
 */
// ---------------------------------------------------------------------------
// Seat count sheet — "Add licenses" (H1b)
// ---------------------------------------------------------------------------
//
// Was: open CheckoutModal with `planCode: sub.planTier` ("expert" — not even a
// real plan code), which ran a FULL CHECKOUT and created a SECOND subscription
// that the Stripe webhook then superseded; the clinic paid once and kept the
// seats at every renewal. Now: one call to POST /api/account/billing/seats,
// which updates the existing Stripe subscription item and prorates.
//
// The preview is computed locally (same helper the checkout uses) but the
// SERVER total is authoritative — the success toast reports the `price` the API
// returns, never the local guess.

interface SeatChangeResponse {
  success: boolean;
  quantity?: number;
  price?: number;
  proratedBy?: "stripe" | "local";
  amountCharged?: number;
  /**
   * Proration credit for removed seats. It goes to the subscription-only
   * credit (spent on the next renewals, never paid out) — not the wallet.
   */
  subscriptionCredited?: number;
  revokedSeats?: number;
  prorationDetails?: {
    daysTotal: number;
    daysUsed: number;
    daysRemaining: number;
    unusedCredit: number;
    newCharge: number;
    netAmount: number;
  };
}

interface SeatsSummaryResponse {
  seats: { total: number; used: number; pending: number; available: number };
}

/** Readable copy for every documented failure of POST /seats. */
function useSeatErrorCopy() {
  const { t } = useLingui();
  const servicesPausedMessage = useServicesPausedBillingMessage();
  return (code: string | undefined, fallback: string | undefined): string => {
    switch (code) {
      case "SERVICES_PAUSED":
        return servicesPausedMessage;
      case "invalid_quantity":
        return t`Pick a whole number of doctors between 1 and ${MAX_CLINIC_DOCTORS}.`;
      case "plan_misconfigured":
        return t`This plan is not set up for per-doctor billing yet. Please contact support.`;
      case "plan_not_purchasable":
        return t`This plan cannot be bought right now.`;
      case "subscription_cancelled":
        return t`This subscription is cancelled — reactivate it before changing seats.`;
      case "no_billing_period":
        return t`This subscription has no active billing period yet.`;
      case "annual_not_available_for_coverage":
        return t`Clinic coverage is billed monthly — annual is not available.`;
      case "subscription_changed":
        return t`This subscription changed in the meantime. Reload and try again.`;
      case "subscription_busy":
        return t`Another change to this subscription is in progress. Please try again in a moment.`;
      case "subscription_lease_unavailable":
        return t`Billing is temporarily unavailable. Please try again in a moment.`;
      case "founding_upgrade_unavailable":
        return t`This change is not available on your Founding plan. Please contact support.`;
      case "payment_failed":
        return t`The payment for this change did not go through, so nothing was changed.`;
      case "payment_not_returned":
        return t`This subscription changed in the meantime and the payment could not be returned automatically. Our team will refund it — please do not try again.`;
      case "payment_processing":
        return t`A payment for this subscription is still being processed. Please try again in a moment.`;
      case "plan_change_scheduled":
        return t`Cancel the scheduled plan change first.`;
      default:
        return fallback || t`Could not change the number of doctors. Please try again.`;
    }
  };
}

function SeatChangeSheet({
  open,
  onClose,
  sub,
  planName,
  onChanged,
  runProtected,
  onShowServicePause,
}: {
  open: boolean;
  onClose: () => void;
  sub: Subscription;
  planName: string;
  onChanged: () => void;
  /** The panel's billing PIN gate: re-asks and retries once on a server vault_locked. */
  runProtected: <T>(call: () => Promise<T>) => Promise<T>;
  /** 409 SERVICES_PAUSED: closes the sheet and shows the pause card. */
  onShowServicePause?: () => void;
}) {
  const { t, i18n } = useLingui();
  const seatErrorCopy = useSeatErrorCopy();
  const pauseCarryMessage = usePauseCarryBillingMessage();
  const pendingPrompt = usePendingPaymentPrompt();
  const [quantity, setQuantity] = useState(Math.max(1, sub.quantity || 1));
  const [error, setError] = useState<string | null>(null);
  // The last refusal was SERVICES_PAUSED — the error box offers the way out.
  const [servicesPaused, setServicesPaused] = useState(false);

  // Reset whenever the sheet is reopened for a (possibly different) row.
  useEffect(() => {
    if (open) {
      setQuantity(Math.max(1, sub.quantity || 1));
      setError(null);
    }
  }, [open, sub.id, sub.quantity]);

  // Plan shape for the local preview, priced at the subscription's own PPP —
  // what the seat change charges.
  const tiersQ = useQuery({
    queryKey: ["api", "/api/account/billing/plan-options", sub.id, i18n.locale],
    queryFn: () =>
      apiFetch.get<{ tiers: PricingTier[] }>(
        `/api/account/billing/plan-options?subscriptionId=${encodeURIComponent(sub.id)}&locale=${i18n.locale}`,
      ),
    enabled: open,
  });
  const planTier = tiersQ.data?.tiers.find((tier) => tier.planCode === sub.plan?.code);
  const seatPriced = !!planTier && hasPerSeatTiers(planTier);

  // Covered doctors today — shared cache with <SubscriptionSeats>.
  const seatsQ = useQuery({
    queryKey: ["account-seats", sub.id],
    queryFn: () =>
      apiFetch.get<SeatsSummaryResponse>(
        `/api/account/seats?subscriptionId=${encodeURIComponent(sub.id)}`,
      ),
    enabled: open,
  });
  // Mirror of the server's `maxQuantityFor(plan)`: 500 for seat-priced clinic
  // plans, 100 for the graduated provider ladder.
  const maxQuantity = seatPriced ? MAX_CLINIC_DOCTORS : MAX_NON_SEAT_QUANTITY;

  const coveredNow = (seatsQ.data?.seats.used ?? 0) + (seatsQ.data?.seats.pending ?? 0);
  const losingCoverage = Math.max(0, coveredNow - quantity);

  const preview = planTier
    ? calculatePlanPrice({
        basePriceMonthly: planTier.priceMonthly,
        basePriceAnnual: planTier.priceAnnual > 0 ? planTier.priceAnnual : undefined,
        quantity,
        interval: sub.planInterval === "annual" ? "annual" : "monthly",
        volumeTiers: planTier.volumeTiers,
        providersIncluded: planTier.providersIncluded ?? 1,
        seatMode: seatPriced ? "flat" : "graduated",
      })
    : null;
  const seatQuote = seatPriced && planTier ? clinicQuote(planTier, quantity) : null;

  const money = (n: number) => formatPrice(n, sub.currency, i18n.locale);

  const sendSeats = () =>
    runProtected(() =>
      apiFetch.post<SeatChangeResponse>("/api/account/billing/seats", {
        subscriptionId: sub.id,
        quantity,
      }),
    );
  const save = useMutation({
    // More seats are billed at once: when the bank asks for 3-D Secure, the
    // customer confirms it and the same change is sent again — Stripe has
    // applied it by then, so it is recorded without a second charge.
    // A declined confirmation discards the held change, so the next one is
    // not refused for a day; an earlier change still held is settled first.
    mutationFn: () =>
      sendChangeWithCardAction(
        sendSeats,
        (secret) => discardPendingPayment(sub.id, runProtected, secret),
        pendingPrompt.ask,
      ),
  });

  const submit = async () => {
    setError(null);
    const handle = showToast(t`Saving…`, "loading");
    try {
      const res = await save.mutateAsync();
      const charged = res.amountCharged ?? 0;
      const credited = res.subscriptionCredited ?? 0;
      const total = money(res.price ?? preview?.intervalTotal ?? 0);
      const n = res.quantity ?? quantity;
      handle.update(
        charged > 0
          ? t`Saved — ${n} doctors, ${total}. Charged ${money(charged)} for the rest of the period.`
          : credited > 0
            ? t`Saved — ${n} doctors, ${total}. ${money(credited)} kept as credit for your next renewals.`
            : t`Saved — ${n} doctors, ${total}.`,
        "success",
      );
      onChanged();
      onClose();
    } catch (err) {
      // The PIN gate was dismissed, or the earlier held payment left as it
      // is: nothing was sent, nothing to report.
      if (isVaultUnlockCancelled(err) || err instanceof PendingChoiceCancelled) {
        handle.dismiss();
        return;
      }
      // The payment went through after all (or nothing certain is known):
      // the page shows the change as it lands.
      if (err instanceof CardActionStillProcessing || err instanceof CardActionOutcomeUnknown) {
        onChanged();
      }
      const body = err instanceof ApiError ? (err.body as { error?: string; code?: string }) : null;
      // A 503 with a code is the lease store, not Stripe (subscription_lease_unavailable).
      const message =
        err instanceof CardActionDeclined
          ? t`The payment was not confirmed, so nothing was changed.`
          : err instanceof CardActionStillProcessing
            ? t`Your payment went through and is still being applied. Refresh in a moment to see the change.`
            : err instanceof CardActionOutcomeUnknown
              ? t`We could not confirm what happened to this payment. Refresh to see the latest state.`
              : err instanceof ApiError && err.status === 503 && !body?.code
                ? t`Payments are not available yet. Please try again later.`
                : body?.code === "PAUSE_CARRY_ACTIVE"
                  ? pauseCarryMessage(err)
                  : seatErrorCopy(body?.code, body?.error);
      setError(message);
      setServicesPaused(isServicesPausedError(err));
      handle.update(message, "error");
    }
  };

  const step = (delta: number) => setQuantity((n) => Math.min(maxQuantity, Math.max(1, n + delta)));

  return (
    <>
      {pendingPrompt.sheet}
      <ResponsiveSheet
        open={open}
        onClose={onClose}
        title={<Trans>Covered doctors</Trans>}
        description={planName}
        width="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" className="min-h-[44px] flex-1" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              className="min-h-[44px] flex-1"
              disabled={save.isPending || quantity === sub.quantity}
              onClick={submit}
            >
              <Trans>Save</Trans>
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-5">
          <p className="text-muted-foreground text-sm">
            <Trans>
              You are billed for {sub.quantity} doctors today. Doctors covered by the clinic pay
              nothing.
            </Trans>
          </p>

          {/* Stepper */}
          <div className="bg-muted/30 flex items-center justify-between gap-3 rounded-xl border p-4">
            <span className="text-sm font-semibold">
              <Trans>Covered doctors</Trans>
            </span>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                aria-label={t`Decrease the number of doctors`}
                onClick={() => step(-1)}
                disabled={quantity <= 1 || save.isPending}
                className="bg-muted hover:bg-muted/70 text-foreground flex size-11 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-30"
              >
                <Minus className="size-4" />
              </button>
              <input
                type="number"
                min={1}
                max={maxQuantity}
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.min(maxQuantity, Math.max(1, parseInt(e.target.value) || 1)))
                }
                className="bg-background text-foreground focus:ring-primary/30 w-20 [appearance:textfield] rounded-xl border px-2 py-2 text-center text-xl font-bold tabular-nums outline-none focus:ring-2 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <button
                type="button"
                aria-label={t`Increase the number of doctors`}
                onClick={() => step(1)}
                disabled={quantity >= maxQuantity || save.isPending}
                className="bg-muted hover:bg-muted/70 text-foreground flex size-11 items-center justify-center rounded-full transition-colors active:scale-95 disabled:opacity-30"
              >
                <Plus className="size-4" />
              </button>
            </div>
          </div>

          {/* Preview — local math, server total wins on save */}
          {tiersQ.isLoading ? (
            <Skeleton className="h-16 w-full rounded-xl" />
          ) : (
            preview && (
              <div className="bg-muted/30 space-y-1 rounded-xl border p-4 text-sm">
                {seatQuote ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        <Trans>Base subscription</Trans>
                      </span>
                      <span className="font-medium tabular-nums">
                        {money(seatQuote.basePriceMonthly)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {quantity} × {money(seatQuote.perSeatPrice)}
                      </span>
                      <span className="font-medium tabular-nums">
                        {money(seatQuote.seatsMonthly)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      <Trans>New total for {quantity} doctors</Trans>
                    </span>
                    <span className="font-medium tabular-nums">{money(preview.monthlyTotal)}</span>
                  </div>
                )}
                <div className="border-border/50 flex justify-between border-t pt-1 font-bold">
                  <span>
                    {sub.planInterval === "annual" ? (
                      <Trans>Total / annual</Trans>
                    ) : (
                      <Trans>Total / monthly</Trans>
                    )}
                  </span>
                  <span className="tabular-nums">{money(preview.intervalTotal)}</span>
                </div>
                <p className="text-muted-foreground text-[0.6875rem]">
                  <Trans>
                    The exact amount is confirmed when you save — prorated for this period.
                  </Trans>
                </p>
              </div>
            )
          )}

          {/* Reducing below the covered headcount revokes the newest seats */}
          {losingCoverage > 0 && (
            <div className="border-warning/30 bg-warning-soft flex items-start gap-2 rounded-xl border p-3">
              <AlertTriangle className="text-warning-strong mt-0.5 size-4 shrink-0" />
              <p className="text-warning-strong text-xs font-medium">
                <Trans>
                  {losingCoverage} doctors will lose coverage — the most recently added ones.
                </Trans>
              </p>
            </div>
          )}

          {error && (
            <div className="border-destructive/30 bg-destructive-soft text-destructive-strong flex items-start gap-2 rounded-xl border p-3 text-xs">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <div className="min-w-0">
                <span>{error}</span>
                {servicesPaused && onShowServicePause && (
                  <div className="mt-2">
                    <GoToServicePauseButton onClick={onShowServicePause} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ResponsiveSheet>
    </>
  );
}

function SubscriptionPanel({ workspace }: { workspace?: BillingWorkspace }) {
  const queryClient = useQueryClient();
  const { i18n, t } = useLingui();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as BillingSubSearch & Record<string, unknown>;
  const { user } = useSession();
  const sessionUser = user as { id?: string; userId?: string; providerId?: string } | null;
  // The PIN asked before an action (triggerProtected) and again when the API
  // refuses the action itself with vault_locked (runProtected, retried once).
  const { triggerProtected, runProtected, PinGateElement } = useProtectedAction("billing");

  const query = useQuery({
    queryKey: BILLING_KEY,
    queryFn: () => apiFetch.get<BillingResponse>("/api/account/billing"),
  });

  // Smart default category for the checkout plan selector (legacy passed it
  // from the server session; the SPA derives it from settings-data).
  const settingsQ = useQuery({
    queryKey: ["api", "/api/account/user/settings-data"],
    queryFn: () =>
      apiFetch.get<{ providerId: string | null; ownedBusinesses: unknown[] }>(
        "/api/account/user/settings-data",
      ),
  });
  // M9 (D2): opened from a clinic workspace → land on the new "Clinic" category
  // so the seat-priced clinic plans are the first thing shown. The workspace is
  // authoritative; the account-wide heuristics below only apply without one.
  const smartDefaultCategory =
    workspace?.entityType === "facility"
      ? "facility"
      : workspace?.entityType === "business"
        ? "business"
        : settingsQ.data?.ownedBusinesses?.length
          ? "business"
          : settingsQ.data?.providerId
            ? "provider"
            : "user";

  // ── Doctor's plan rows: one card each (Wave 2b, §11.7a) ───────────────
  // A doctor can hold his own plan and be covered by a clinic at the same
  // time, and `/api/account/billing` only ever lists rows the CALLER pays for
  // — so the clinic's row was invisible here while the gate honoured it.
  //
  // `/account/provider/billing` says which workspace it is. The two payer-level
  // URLs (`/account/billing`, `/account/user/billing`) pass none, and a doctor
  // opening those must still see who pays for what — so a caller with a
  // provider profile gets the same cards there. Facility/business workspaces
  // always pass a workspace, so they are never in this branch.
  const isProviderWorkspace =
    workspace?.entityType === "provider" || (!workspace && !!sessionUser?.providerId);
  const planRowsQ = useProviderPlanRows(isProviderWorkspace);
  const planRows = planRowsQ.data?.rows ?? [];
  const ownPlanRowBySubId = new Map(
    planRows.filter((r) => r.origin === "own").map((r) => [r.subscriptionId, r]),
  );
  // The gate returns ONE row per subscription across origins (Wave 2a), so a
  // coverage row can never be a duplicate of an own row here.
  const coverageRows = planRows.filter((r) => r.origin === "coverage");

  // "Pause services" belongs to the workspace entity (doctor / clinic /
  // business); the payer-level URLs have none. The doctor workspace carries no
  // id — it is the caller's own provider profile.
  const pauseEntityId =
    workspace?.entityId ??
    (workspace?.entityType === "provider" ? (settingsQ.data?.providerId ?? undefined) : undefined);
  const pauseEntity =
    workspace && workspace.entityType !== "user" && pauseEntityId
      ? { entityType: workspace.entityType, entityId: pauseEntityId }
      : null;

  const [detailSubId, setDetailSubId] = useState<string | null>(search.subId ?? null);
  const [checkoutModal, setCheckoutModal] = useState<CheckoutModalState>({ open: false });
  // Subscription whose covered-doctor count is being edited (H1b).
  const [seatsSheetSubId, setSeatsSheetSubId] = useState<string | null>(null);
  // Subscription whose plan is being changed (ChangePlanSheet).
  const [changePlanSubId, setChangePlanSubId] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState<string | null>(null);
  const [oneClick, setOneClick] = useState<{
    action: "upgrade" | "reactivate" | "retry_payment";
    subscriptionId?: string;
    newPlanCode?: string;
  } | null>(null);

  // Derive view from the subtab URL param (legacy convention).
  const view: SubscriptionView = search.subtab === "detail" ? "detail" : "summary";

  const setView = (newView: SubscriptionView, extra?: Record<string, string>) => {
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        if (newView === "summary") {
          delete next.subtab;
          delete next.category;
        } else {
          next.subtab = newView;
        }
        if (extra) Object.assign(next, extra);
        return next;
      },
      replace: false,
    });
  };

  // 409 SERVICES_PAUSED from a billing action (_docs/service-pause.md): close
  // whatever covers the page, back to the summary, and bring the pause card —
  // with its "Restart now" — into view.
  const servicesPausedMessage = useServicesPausedBillingMessage();
  const [pauseScrollRequest, setPauseScrollRequest] = useState(0);
  const showServicePause = () => {
    setCheckoutModal({ open: false });
    setChangePlanSubId(null);
    setSeatsSheetSubId(null);
    setOneClick(null);
    // The refusal says the entity is paused: the card must not say otherwise.
    void queryClient.invalidateQueries({ queryKey: SERVICE_PAUSE_KEY_ROOT });
    if (view !== "summary") setView("summary");
    setPauseScrollRequest((n) => n + 1);
  };
  const onShowServicePause = pauseEntity ? showServicePause : undefined;
  useEffect(() => {
    if (pauseScrollRequest === 0) return;
    // After the sheet's exit animation has released the scroll lock.
    const timer = window.setTimeout(scrollToServicePauseCard, 400);
    return () => window.clearTimeout(timer);
  }, [pauseScrollRequest]);

  // Auto-open checkout when navigated with ?category= (once only) — legacy
  // deep-link from pricing pages.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (
      !query.isLoading &&
      search.category &&
      search.subtab !== "detail" &&
      !autoOpenedRef.current
    ) {
      autoOpenedRef.current = true;
      setCheckoutModal({ open: true });
    }
  }, [query.isLoading, search.category, search.subtab]);

  // P23: the two most consequential billing actions report through the top
  // toast. A dismissed PIN gate closes the toast silently (not an error).
  const cancel = useMutation({
    mutationFn: (sub: Subscription) => {
      const accessUntil = formatDate(sub.currentPeriodEnd);
      return withProtectedSavingToast(
        () =>
          runProtected(() =>
            apiFetch.post("/api/account/billing/cancel", { subscriptionId: sub.id }),
          ),
        {
          saving: t`Cancelling subscription…`,
          saved: sub.currentPeriodEnd
            ? t`Subscription cancelled — access until ${accessUntil}`
            : t`Subscription cancelled`,
          error: t`Could not cancel. Try again.`,
        },
      );
    },
    onSuccess: () => {
      setShowCancelConfirm(null);
      queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    },
  });

  const reactivate = useMutation({
    mutationFn: (subscriptionId: string) =>
      withProtectedSavingToast(
        () =>
          runProtected(() => apiFetch.post("/api/account/billing/reactivate", { subscriptionId })),
        {
          saving: t`Reactivating subscription…`,
          saved: t`Subscription reactivated`,
          error: (err) =>
            isServicesPausedError(err)
              ? servicesPausedMessage
              : t`Could not reactivate. Try again.`,
        },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BILLING_KEY }),
    onError: (err) => {
      // A toast has no button: take the owner to the pause card instead.
      if (isServicesPausedError(err)) onShowServicePause?.();
    },
  });

  // Legacy: wallet auto-pay is a toggle (Switch), not read-only.
  const walletToggle = useMutation({
    mutationFn: (enabled: boolean) =>
      withSavingToast(() => apiFetch.post("/api/account/billing/wallet-toggle", { enabled }), {
        saving: t`Saving…`,
        saved: t`Saved`,
        error: t`Could not save changes`,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BILLING_KEY }),
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(i18n.locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const fmt = (amount: number, currency: string) => formatPrice(amount, currency, i18n.locale);

  // A plan change (or the scheduled one kept off) rewrites the row, the
  // doctor's plan cards and the summary card's own inventory read.
  const refreshAfterPlanChange = (subscriptionId: string) => {
    void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
    void queryClient.invalidateQueries({ queryKey: PROVIDER_PLAN_ROWS_KEY });
    void queryClient.invalidateQueries({
      queryKey: ["api", `/api/account/subscriptions/${subscriptionId}/inventory`],
    });
  };

  if (query.isLoading) return <PanelSkeleton tiles={2} />;
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;

  const data = query.data!;
  const subs = data.subscriptions;
  const walletBalance = data.wallet.balance || 0;
  const subscriptionCredit = data.wallet.subscriptionCredit || 0;
  const totalMonthly = subs
    .filter((s) => s.status === "active")
    .reduce((sum, s) => sum + (s.planInterval === "annual" ? s.price / 12 : s.price), 0);

  const activeDetailId = detailSubId ?? search.subId ?? null;
  const detailSub = activeDetailId ? subs.find((s) => s.id === activeDetailId) : null;
  const seatsSheetSub = seatsSheetSubId ? subs.find((s) => s.id === seatsSheetSubId) : null;
  const changePlanSub = changePlanSubId ? subs.find((s) => s.id === changePlanSubId) : null;
  // One doctor's own plan: its single licence already covers him, so the seat
  // inventory means nothing there. "Add licenses" stays — a doctor plan sells
  // extra doctors (the plan's volume ladder), and this is its only entry point.
  const isSingleDoctorPlan = (sub: Subscription) =>
    sub.entityType === "provider" && sub.quantity === 1;
  const hasScheduledChange = (sub: Subscription) => !!sub.scheduledChange && !sub.cancelledAt;

  // D2: never print `planTier` as-is — it is the code suffix ("expert") and the
  // plan is sold as "Premium". `pricing_plans.name` is the label; the capitalised
  // tier word is the last-resort fallback for a deleted plan row.
  const planDisplayName = (sub: Subscription) =>
    sub.plan?.name?.[i18n.locale] ||
    sub.plan?.name?.en ||
    sub.plan?.name?.ro ||
    (sub.planTier ? sub.planTier.charAt(0).toUpperCase() + sub.planTier.slice(1) : "");

  const noSubscriptionState = (
    <div className="mt-6 flex flex-col items-center rounded-lg border-2 border-dashed py-10">
      <Crown className="text-muted-foreground/40 size-10" />
      <p className="text-muted-foreground mt-3 text-sm font-medium">
        <Trans>No active subscription</Trans>
      </p>
      <p className="text-muted-foreground/70 mt-1 text-xs">
        <Trans>Choose a plan to unlock all features</Trans>
      </p>
      <Button size="touch" className="mt-4" onClick={() => setCheckoutModal({ open: true })}>
        <Trans>Choose a plan</Trans>
      </Button>
    </div>
  );

  return (
    <>
      <AnimatePresence mode="wait">
        {/* ═════════════ SUMMARY VIEW ═════════════ */}
        {view === "summary" && (
          <m.div
            key="summary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4 pb-6"
          >
            <div className="bg-card rounded-xl border p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crown className="text-primary size-5" />
                  <h2 className="text-foreground text-lg font-semibold">
                    <Trans>Subscriptions</Trans>
                  </h2>
                </div>
                {subs.length > 0 && (
                  <Button
                    variant="outline"
                    size="touch"
                    onClick={() => setCheckoutModal({ open: true })}
                  >
                    <Plus className="size-4" aria-hidden />
                    <Trans>Add subscription</Trans>
                  </Button>
                )}
              </div>

              {subs.length === 0 && coverageRows.length === 0 ? (
                workspace?.entityType === "provider" ? (
                  // A doctor who pays for nothing is on the Free plan — show it
                  // (from the tiers data) instead of "no subscription".
                  <FreePlanCard
                    entityType="provider"
                    onSeePaidPlans={() =>
                      setCheckoutModal({ open: true, entityType: "provider", category: "provider" })
                    }
                    fallback={noSubscriptionState}
                  />
                ) : (
                  noSubscriptionState
                )
              ) : (
                <div className="mt-4 space-y-4">
                  {/* Expanded cards per subscription — seats + packs + actions inline. */}
                  {subs.map((sub, idx) => {
                    // Only a row the plan resolver recognises as this doctor's
                    // own gets the payer line; every other workspace keeps the
                    // header it has always had.
                    const ownRow = isProviderWorkspace ? ownPlanRowBySubId.get(sub.id) : undefined;
                    return (
                      <m.div
                        key={sub.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <SubscriptionExpandedCard
                          subscriptionId={sub.id}
                          payerNote={ownRow ? <Trans>· paid by you</Trans> : undefined}
                          headerNote={
                            ownRow && sub.isFounding ? (
                              sub.currentPeriodEnd ? (
                                <Trans>Free until {formatDate(sub.currentPeriodEnd)}</Trans>
                              ) : (
                                <Trans>Free for life</Trans>
                              )
                            ) : undefined
                          }
                          onChangePlan={() => {
                            setDetailSubId(sub.id);
                            setView("detail", { subId: sub.id });
                          }}
                          onCancelSubscription={() => {
                            setDetailSubId(sub.id);
                            setView("detail", { subId: sub.id });
                          }}
                        />
                        {hasScheduledChange(sub) && (
                          <ScheduledChangeNotice
                            className="mt-2"
                            sub={sub}
                            currentPlanName={planDisplayName(sub)}
                            onChanged={() => refreshAfterPlanChange(sub.id)}
                          />
                        )}
                      </m.div>
                    );
                  })}

                  {/* Clinic coverage — read-only, nothing here is his to cancel. */}
                  {coverageRows.map((row, idx) => (
                    <m.div
                      key={`coverage:${row.subscriptionId}:${row.context?.facilityId ?? ""}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (subs.length + idx) * 0.05 }}
                    >
                      <CoveragePlanCard row={row} />
                    </m.div>
                  ))}

                  {subs.filter((s) => s.status === "active").length > 1 && (
                    <div className="text-muted-foreground flex items-center justify-between border-t pt-3 text-sm">
                      <span>
                        <Trans>Total monthly</Trans>
                      </span>
                      <span className="text-foreground font-semibold tabular-nums">
                        ~{fmt(totalMonthly, "EUR")}
                        <Trans>/month</Trans>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {pauseEntity && (
              // Scroll target of `showServicePause` (a billing action refused
              // because services are paused).
              <div id={SERVICE_PAUSE_CARD_ID} tabIndex={-1} className="scroll-mt-24 outline-none">
                <ServicePauseCard
                  {...pauseEntity}
                  planNames={Object.fromEntries(subs.map((sub) => [sub.id, planDisplayName(sub)]))}
                />
              </div>
            )}

            {(walletBalance > 0 || subscriptionCredit > 0) && (
              <div className="bg-card rounded-xl border p-4 sm:p-6">
                <div className="flex items-center gap-2">
                  <Wallet className="text-primary size-5" />
                  <h2 className="text-foreground text-lg font-semibold">
                    <Trans>Wallet credit</Trans>
                  </h2>
                </div>
                {walletBalance > 0 && (
                  <div className="mt-4 flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <p className="text-foreground text-sm font-medium">
                        <Trans>Pay from wallet balance</Trans>
                      </p>
                      <p className="text-muted-foreground text-xs">
                        <Trans>Available: {fmt(walletBalance, "EUR")}</Trans>
                      </p>
                    </div>
                    <Switch
                      checked={data.wallet.payFromWallet}
                      onCheckedChange={(enabled) => walletToggle.mutate(enabled)}
                      disabled={walletToggle.isPending}
                      aria-label={t`Pay from wallet balance`}
                    />
                  </div>
                )}
                {subscriptionCredit > 0 && (
                  <div className="mt-4 rounded-lg border p-4">
                    <p className="text-foreground text-sm font-medium">
                      <Trans>Subscription credit: {fmt(subscriptionCredit, "EUR")}</Trans>
                    </p>
                    <p className="text-muted-foreground text-xs">
                      <Trans>
                        Unused time from a plan change. It pays your next renewals automatically and
                        cannot be withdrawn.
                      </Trans>
                    </p>
                  </div>
                )}
              </div>
            )}
          </m.div>
        )}

        {/* ═════════════ DETAIL VIEW — single subscription ═════════════ */}
        {view === "detail" && detailSub && (
          <m.div
            key="detail"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="pb-6"
          >
            <button
              type="button"
              onClick={() => setView("summary")}
              className="text-muted-foreground hover:text-foreground mb-4 flex min-h-[44px] items-center gap-1 text-sm transition-colors"
            >
              <ArrowLeft className="size-4" />
              <Trans>Back to subscriptions</Trans>
            </button>

            <div className="bg-card rounded-xl border p-4 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-foreground text-lg font-semibold">
                      {planDisplayName(detailSub)}
                    </h2>
                    <StatusBadge status={detailSub.status} />
                    {detailSub.isFounding && (
                      <Badge variant="outline" className="border-amber-300 text-amber-600">
                        <Trans>Founding Doctor</Trans>
                      </Badge>
                    )}
                  </div>
                  {/* A founding row is free for a fixed term — "€0 /month ·
                      Monthly" reads like a billed plan, so it is replaced by
                      the term itself below. */}
                  {detailSub.isFounding ? (
                    <p className="text-muted-foreground mt-1 text-sm">
                      {detailSub.currentPeriodEnd ? (
                        <Trans>Free until {formatDate(detailSub.currentPeriodEnd)}</Trans>
                      ) : (
                        <Trans>Free for life</Trans>
                      )}
                    </p>
                  ) : (
                    <p className="text-muted-foreground mt-1 text-sm">
                      {fmt(detailSub.price, detailSub.currency)}
                      {detailSub.planInterval === "annual" ? (
                        <Trans>/year</Trans>
                      ) : (
                        <Trans>/month</Trans>
                      )}
                      {" · "}
                      {detailSub.planInterval === "annual" ? (
                        <Trans>Annual</Trans>
                      ) : (
                        <Trans>Monthly</Trans>
                      )}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  {detailSub.status === "active" &&
                    detailSub.currentPeriodEnd &&
                    !detailSub.cancelledAt &&
                    (detailSub.isFounding ? (
                      <p className="text-muted-foreground text-xs">
                        <Trans>Free until {formatDate(detailSub.currentPeriodEnd)}</Trans>
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        <Trans>Renews on {formatDate(detailSub.currentPeriodEnd)}</Trans>
                      </p>
                    ))}
                  {detailSub.cancelledAt && detailSub.currentPeriodEnd && (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <p className="text-warning-strong text-xs">
                        <Trans>Ends on {formatDate(detailSub.currentPeriodEnd)}</Trans>
                      </p>
                      {detailSub.status === "active" && (
                        <Button
                          variant="outline"
                          size="touch"
                          onClick={() => reactivate.mutate(detailSub.id)}
                          disabled={reactivate.isPending}
                        >
                          {reactivate.isPending && (
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                          )}
                          <Trans>Reactivate</Trans>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* A downgrade waiting for the period end — "Keep" cancels it. */}
              {hasScheduledChange(detailSub) && (
                <ScheduledChangeNotice
                  className="mt-4"
                  sub={detailSub}
                  currentPlanName={planDisplayName(detailSub)}
                  onChanged={() => refreshAfterPlanChange(detailSub.id)}
                />
              )}

              {/* Seat allocations — not for one doctor's own plan. */}
              {detailSub.quantity > 0 && !isSingleDoctorPlan(detailSub) && (
                <div className="mt-4">
                  <SubscriptionSeats
                    sub={{
                      id: detailSub.id,
                      planTier: detailSub.planTier,
                      status: detailSub.status,
                      quantity: detailSub.quantity,
                      plan: detailSub.plan,
                    }}
                    hasProviderId={!!sessionUser?.providerId}
                    userId={sessionUser?.userId ?? sessionUser?.id}
                  />
                </div>
              )}

              {/* Past due */}
              {detailSub.status === "past_due" && (
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-amber-50 p-3 dark:bg-amber-950/30">
                  <AlertTriangle className="size-4 text-amber-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-400">
                      <Trans>Payment failed</Trans>
                    </p>
                    <p className="text-xs text-amber-600">
                      <Trans>
                        Your last payment failed. Please update your payment method to avoid losing
                        access.
                      </Trans>
                    </p>
                  </div>
                  <Button
                    size="touch"
                    onClick={() =>
                      triggerProtected(() =>
                        setOneClick({ action: "retry_payment", subscriptionId: detailSub.id }),
                      )
                    }
                  >
                    <Trans>Retry payment</Trans>
                  </Button>
                </div>
              )}

              {/* Actions */}
              {detailSub.status === "active" && !detailSub.cancelledAt && !detailSub.isFounding && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="touch"
                    onClick={() => triggerProtected(() => setChangePlanSubId(detailSub.id))}
                  >
                    <Trans>Change plan</Trans> <ChevronRight className="size-4" aria-hidden />
                  </Button>
                  {/* H1b: changes the EXISTING subscription's seat count
                      (POST /api/account/billing/seats) instead of running a
                      second checkout for the same plan. The API refuses it
                      while a plan change is scheduled (plan_change_scheduled). */}
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      size="touch"
                      disabled={hasScheduledChange(detailSub)}
                      onClick={() => triggerProtected(() => setSeatsSheetSubId(detailSub.id))}
                    >
                      <Trans>Add licenses</Trans>
                    </Button>
                    {hasScheduledChange(detailSub) && (
                      <p className="text-muted-foreground text-xs">
                        <Trans>Cancel the scheduled plan change first.</Trans>
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="touch"
                    className="text-destructive"
                    onClick={() => triggerProtected(() => setShowCancelConfirm(detailSub.id))}
                  >
                    <Trans>Cancel subscription</Trans>
                  </Button>
                </div>
              )}

              {/* Reactivate (fully cancelled) */}
              {detailSub.status === "cancelled" && (
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="touch"
                    onClick={() =>
                      triggerProtected(() =>
                        setOneClick({ action: "reactivate", subscriptionId: detailSub.id }),
                      )
                    }
                  >
                    <Trans>Reactivate</Trans>
                  </Button>
                </div>
              )}

              {/* Cancel confirmation */}
              <AnimatePresence>
                {showCancelConfirm === detailSub.id && (
                  <m.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-destructive/20 bg-destructive/5 mt-4 overflow-hidden rounded-lg border p-4"
                  >
                    <p className="text-foreground text-sm font-medium">
                      <Trans>Cancel subscription?</Trans>
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      <Trans>
                        You'll keep access until the end of your billing period (
                        {formatDate(detailSub.currentPeriodEnd)}). After that, you'll be downgraded
                        to the free plan.
                      </Trans>
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="touch"
                        variant="destructive"
                        onClick={() => cancel.mutate(detailSub)}
                        disabled={cancel.isPending}
                      >
                        {cancel.isPending ? (
                          <Loader2 className="size-4 animate-spin" aria-hidden />
                        ) : (
                          <X className="size-4" aria-hidden />
                        )}
                        <Trans>Yes, cancel</Trans>
                      </Button>
                      <Button
                        size="touch"
                        variant="outline"
                        onClick={() => setShowCancelConfirm(null)}
                      >
                        <Check className="size-4" aria-hidden />
                        <Trans>Keep plan</Trans>
                      </Button>
                    </div>
                  </m.div>
                )}
              </AnimatePresence>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      {/* Covered-doctor count for an existing row (H1b). */}
      {seatsSheetSub && (
        <SeatChangeSheet
          open
          onClose={() => setSeatsSheetSubId(null)}
          sub={seatsSheetSub}
          planName={planDisplayName(seatsSheetSub)}
          runProtected={runProtected}
          onShowServicePause={onShowServicePause}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
            void queryClient.invalidateQueries({ queryKey: PROVIDER_PLAN_ROWS_KEY });
            void queryClient.invalidateQueries({ queryKey: ["account-seats", seatsSheetSub.id] });
          }}
        />
      )}

      {/* Plan change for an existing row: a pricier plan goes through the
          one-click upgrade (prorated preview → confirm), a cheaper one is
          confirmed in the sheet. */}
      {changePlanSub && (
        <ChangePlanSheet
          open
          onClose={() => setChangePlanSubId(null)}
          sub={changePlanSub}
          currentPlanName={planDisplayName(changePlanSub)}
          onUpgrade={(newPlanCode) => {
            setChangePlanSubId(null);
            setOneClick({ action: "upgrade", subscriptionId: changePlanSub.id, newPlanCode });
          }}
          onChanged={() => refreshAfterPlanChange(changePlanSub.id)}
          onShowServicePause={onShowServicePause}
        />
      )}

      {/* In-site checkout modal — new purchases only (plan changes use the sheet above). */}
      <CheckoutModal
        open={checkoutModal.open}
        onClose={() => setCheckoutModal({ open: false })}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: BILLING_KEY })}
        planCode={checkoutModal.planCode}
        planData={checkoutModal.planData}
        entityType={
          (checkoutModal.entityType || "user") as "provider" | "facility" | "business" | "user"
        }
        workspace={workspace}
        initialInterval={checkoutModal.interval}
        defaultCategory={checkoutModal.category ?? smartDefaultCategory}
        onShowServicePause={onShowServicePause}
      />

      {PinGateElement}

      {oneClick && (
        <OneClickPaymentModal
          open={!!oneClick}
          onClose={() => setOneClick(null)}
          onSuccess={() => {
            if (oneClick.subscriptionId) refreshAfterPlanChange(oneClick.subscriptionId);
            else void queryClient.invalidateQueries({ queryKey: BILLING_KEY });
            setOneClick(null);
          }}
          action={oneClick.action}
          subscriptionId={oneClick.subscriptionId}
          newPlanCode={oneClick.newPlanCode}
          onShowServicePause={onShowServicePause}
        />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: Subscription["status"] }) {
  const map: Record<Subscription["status"], { tone: string; label: React.ReactNode }> = {
    active: {
      tone: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      label: <Trans>Active</Trans>,
    },
    trialing: {
      tone: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      label: <Trans>Trial</Trans>,
    },
    past_due: {
      tone: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
      label: <Trans>Past due</Trans>,
    },
    cancelled: {
      tone: "bg-muted text-muted-foreground",
      label: <Trans>Cancelled</Trans>,
    },
    paused: {
      tone: "bg-warning-soft text-warning-strong",
      label: <Trans>Paused</Trans>,
    },
  };
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[0.625rem] font-semibold tracking-wide uppercase",
        map[status].tone,
      )}
    >
      {map[status].label}
    </span>
  );
}

const CARDS_KEY = ["api", "/api/account/billing/cards"] as const;

const BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
  diners: "Diners",
  jcb: "JCB",
  unionpay: "UnionPay",
};

/**
 * 1:1 port of legacy `WalletCardsTab` (src/components/account/wallet/
 * cards-tab.tsx): PIN-protected (feature "wallet"), Stripe security
 * disclaimer, brand chip, star-to-set-default, inline delete confirm,
 * add-card via Stripe Elements.
 */
function CardsPanel() {
  return (
    <ProtectedContent feature="wallet">
      <CardsContent />
    </ProtectedContent>
  );
}

// The API refuses every /cards route with 403 vault_locked while the wallet
// PIN is not unlocked (apps/api account-billing-cards.ts). The list read hands
// such a refusal to ProtectedContent (handleVaultLocked: the gate comes back,
// and the list refetches when it remounts after the unlock); the writes go
// through runUnlocked, which asks again and retries the write once. Their
// follow-up lives in the mutation-level callbacks, which still run if the
// panel unmounted behind the gate meanwhile.
function CardsContent() {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { runUnlocked } = useVaultLock();
  const [showAddForm, setShowAddForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: CARDS_KEY,
    queryFn: () =>
      apiFetch.get<CardsResponse>("/api/account/billing/cards").catch((err: unknown) => {
        handleVaultLocked(err);
        throw err;
      }),
    retry: (failures, err) => !isVaultLockedError(err) && failures < 3,
  });

  const setDefault = useMutation({
    mutationFn: (paymentMethodId: string) =>
      runUnlocked(() => apiFetch.post("/api/account/billing/cards/default", { paymentMethodId })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CARDS_KEY });
      showToast(t`Default card set`, "success");
    },
    onError: (err) => {
      if (!isVaultUnlockCancelled(err)) showToast(t`Error saving`, "error");
    },
  });

  // apiFetch.delete (not raw) so a refusal — vault_locked included — throws
  // instead of reading as "Card deleted".
  const remove = useMutation({
    mutationFn: (paymentMethodId: string) =>
      runUnlocked(() => apiFetch.delete("/api/account/billing/cards", { paymentMethodId })),
    onSuccess: () => {
      setConfirmDeleteId(null);
      queryClient.invalidateQueries({ queryKey: CARDS_KEY });
      showToast(t`Card deleted`, "success");
    },
    onError: (err) => {
      if (!isVaultUnlockCancelled(err)) showToast(t`Error saving`, "error");
    },
  });

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="bg-muted h-20 animate-pulse rounded-xl" />
        ))}
      </div>
    );
  }
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;

  const { cards, defaultPaymentMethodId } = query.data!;
  const handleAddSuccess = () => {
    setShowAddForm(false);
    queryClient.invalidateQueries({ queryKey: CARDS_KEY });
  };

  return (
    <div className="space-y-6 pb-6">
      {/* Security disclaimer */}
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
        <Shield className="mt-0.5 size-5 shrink-0 text-blue-600 dark:text-blue-400" />
        <div>
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
            <Trans>Cards are securely stored at Stripe</Trans>
          </p>
          <p className="mt-0.5 text-xs text-blue-600 dark:text-blue-400">
            <Trans>
              PontiMed does not store or have access to your card data. All transactions are
              processed by Stripe.
            </Trans>
          </p>
        </div>
      </div>

      {/* Card list */}
      {cards.length === 0 && !showAddForm ? (
        <div className="flex flex-col items-center rounded-lg border-2 border-dashed py-10">
          <CreditCard className="text-muted-foreground/40 size-10" />
          <p className="text-muted-foreground mt-3 text-sm font-medium">
            <Trans>No cards added</Trans>
          </p>
          <Button className="mt-4" onClick={() => setShowAddForm(true)}>
            <Plus className="mr-1.5 size-4" />
            <Trans>Add card</Trans>
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {cards.map((card, index) => {
              const isDefault = card.id === defaultPaymentMethodId;
              return (
                <m.div
                  key={card.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={cn(
                    "bg-card flex items-center justify-between rounded-xl border p-4",
                    isDefault && "border-primary/30",
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-muted flex h-10 w-14 items-center justify-center rounded-lg text-xs font-bold">
                      {BRAND_LABELS[card.brand] || card.brand}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-foreground text-sm font-medium">•••• {card.last4}</p>
                        {isDefault && (
                          <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[10px] font-medium">
                            <Trans>Default</Trans>
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-xs">
                        <Trans>Expires</Trans> {String(card.expMonth).padStart(2, "0")}/
                        {card.expYear}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    {!isDefault && (
                      <Button
                        variant="ghost"
                        size="icon-touch"
                        onClick={() => setDefault.mutate(card.id)}
                        disabled={setDefault.isPending}
                        aria-label={t`Set default`}
                      >
                        <Star className="size-3.5" />
                      </Button>
                    )}
                    {confirmDeleteId === card.id ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="destructive"
                          size="touch"
                          onClick={() => remove.mutate(card.id)}
                          disabled={remove.isPending}
                        >
                          {remove.isPending && (
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                          )}
                          <Trans>Confirm</Trans>
                        </Button>
                        <Button
                          variant="ghost"
                          size="touch"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          <Trans>Cancel</Trans>
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon-touch"
                        className="text-destructive"
                        onClick={() => setConfirmDeleteId(card.id)}
                        aria-label={t`Remove card`}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    )}
                  </div>
                </m.div>
              );
            })}
          </div>

          {!showAddForm && (
            <Button variant="outline" size="touch" onClick={() => setShowAddForm(true)}>
              <Plus className="size-4" aria-hidden />
              <Trans>Add card</Trans>
            </Button>
          )}
        </>
      )}

      {/* Add card form */}
      <AnimatePresence>
        {showAddForm && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <StripeElementsProvider>
              <AddCardForm onSuccess={handleAddSuccess} onCancel={() => setShowAddForm(false)} />
            </StripeElementsProvider>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PanelSkeleton({ tiles }: { tiles: number }) {
  return (
    <div className="space-y-3 pb-6">
      {Array.from({ length: tiles }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full rounded-xl" />
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <LoadErrorState
      className="mt-2"
      title={<Trans>Could not load billing.</Trans>}
      onRetry={onRetry}
    />
  );
}
